import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { getPeopleCount } from "@/lib/ticket-utils";

export interface DailyTicketMedio {
  date: string;
  totalPeople: number;
  totalSales: number;
  orderCount: number;
  ticketPerPerson: number;
  ticketPerOrder: number;
}

export interface TicketMedioSummary {
  totalPeople: number;
  totalSales: number;
  totalOrders: number;
  ticketPerPerson: number;
  ticketPerOrder: number;
  dailyData: DailyTicketMedio[];
}

interface UseTicketMedioReportParams {
  dateFrom: string;
  dateTo: string;
  periodId?: string;
}

export function useTicketMedioReport({ dateFrom, dateTo, periodId }: UseTicketMedioReportParams) {
  const { activeRestaurant } = useRestaurantContext();

  return useQuery({
    queryKey: ["ticket-medio-report", activeRestaurant?.id, dateFrom, dateTo, periodId],
    queryFn: async (): Promise<TicketMedioSummary> => {
      if (!activeRestaurant) {
        return {
          totalPeople: 0,
          totalSales: 0,
          totalOrders: 0,
          ticketPerPerson: 0,
          ticketPerOrder: 0,
          dailyData: [],
        };
      }

      const startISO = `${dateFrom}T00:00:00.000Z`;
      const endISO = `${dateTo}T23:59:59.999Z`;

      // Build the query
      let query = supabase
        .from("orders")
        .select("id, total, people_count, order_type, created_at, status")
        .eq("restaurant_id", activeRestaurant.id)
        .neq("status", "cancelled")
        .order("created_at", { ascending: true });

      // Filter by period_id if provided, otherwise use date range
      if (periodId) {
        query = query.eq("period_id", periodId);
      } else {
        query = query.gte("created_at", startISO).lte("created_at", endISO);
      }

      const { data: orders, error } = await query;

      if (error) throw error;

      // Group by date
      const dailyMap = new Map<string, { people: number; sales: number; orders: number }>();
      
      let totalPeople = 0;
      let totalSales = 0;

      (orders || []).forEach((order) => {
        const date = new Date(order.created_at).toISOString().split("T")[0];
        const current = dailyMap.get(date) || { people: 0, sales: 0, orders: 0 };
        
        const people = getPeopleCount(order);
        const total = Number(order.total) || 0;
        
        current.sales += total;
        current.orders += 1;
        current.people += people;
        totalPeople += people;
        
        totalSales += total;
        
        dailyMap.set(date, current);
      });

      const dailyData: DailyTicketMedio[] = Array.from(dailyMap.entries()).map(([date, data]) => ({
        date,
        totalPeople: data.people,
        totalSales: data.sales,
        orderCount: data.orders,
        ticketPerPerson: data.people > 0 ? data.sales / data.people : 0,
        ticketPerOrder: data.orders > 0 ? data.sales / data.orders : 0,
      }));

      return {
        totalPeople,
        totalSales,
        totalOrders: orders?.length || 0,
        ticketPerPerson: totalPeople > 0 ? totalSales / totalPeople : 0,
        ticketPerOrder: orders && orders.length > 0 ? totalSales / orders.length : 0,
        dailyData,
      };
    },
    enabled: !!activeRestaurant,
  });
}
